import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import type { SessionDetailDto, WireUserDto } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { refreshSessions } from '../lib/sessionCache.ts';
import { Avatar, ChannelBadge, Spinner } from './ui.tsx';
import { Select } from './Select.tsx';
import { XIcon } from './icons.tsx';

interface AgentRow {
  invitationUuid: string | null;
  userId: number | null;
  email: string;
  name: string;
  role: string;
  status: 'active' | 'pending';
}

/** Right-hand context panel: who the customer is, and who owns the thread. */
export function SessionDetails({
  session,
  onClose,
}: {
  session: SessionDetailDto;
  onClose: () => void;
}): React.JSX.Element {
  const { isManager } = useAuth();
  const queryClient = useQueryClient();

  const agents = useQuery({
    queryKey: ['management', 'agents'],
    queryFn: () => api.get<{ items: AgentRow[] }>('/management/agents'),
    // Only managers can reassign, and only they can read this endpoint.
    enabled: isManager,
    staleTime: 5 * 60_000,
  });

  const assign = useMutation({
    mutationFn: (assignedToId: number | null) =>
      api.post(`/sessions/${session.uuid}/assign`, { assignedToId }),
    onSuccess: () => {
      refreshSessions(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['session', session.uuid] });
    },
  });

  return (
    <>
      {/*
        Below lg this panel is a drawer over the thread rather than a column
        beside it. It used to carry `hidden lg:flex`, which meant that on a
        tablet the toggle mounted a panel with display:none — and since this is
        the only place a manager can reassign a conversation, reassignment was
        impossible on any viewport under 1024px.
      */}
      <div
        className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden"
        aria-hidden="true"
        onClick={onClose}
      />

      <aside
        className="fixed inset-y-0 right-0 z-40 flex w-80 max-w-[85vw] shrink-0 flex-col border-l border-slate-200 bg-white shadow-xl
                   lg:static lg:z-auto lg:w-72 lg:max-w-none lg:shadow-none
                   dark:border-slate-800 dark:bg-slate-900"
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">Details</h2>
          <button type="button" className="lg-btn-ghost px-2" onClick={onClose}>
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close details</span>
          </button>
        </header>

        <div className="lg-scroll min-h-0 flex-1 overflow-y-auto p-4">
          <div className="flex flex-col items-center text-center">
            <Avatar src={session.customerAvatar} name={session.customerName} size={64} />
            <p className="mt-3 text-sm font-semibold text-slate-900 dark:text-slate-50">
              {session.customerName}
            </p>
            <div className="mt-2">
              <ChannelBadge type={session.type} />
            </div>
          </div>

          <dl className="mt-6 space-y-3 text-sm">
            <Row label="Email" value={session.customerEmail} />
            <Row label="Phone" value={session.customerPhone} />
            <Row
              label="Started"
              value={new Date(session.createdAt).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            />
            <Row
              label="Status"
              value={session.spam ? 'Spam' : session.archived ? 'Archived' : 'Open'}
            />
          </dl>

          {session.unsubscribed.email || session.unsubscribed.sms ? (
            <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
              This customer opted out of{' '}
              {[session.unsubscribed.email && 'email', session.unsubscribed.sms && 'SMS']
                .filter(Boolean)
                .join(' and ')}
              . Offline replies on that channel will not be delivered.
            </p>
          ) : null}

          <div className="mt-6">
            <h3 className="lg-label">Assigned to</h3>

            {!isManager ? (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {session.assignedTo?.displayName ?? 'Unassigned'}
              </p>
            ) : agents.isLoading ? (
              <Spinner className="h-4 w-4 text-brand-600" />
            ) : (
              // 0 stands in for "nobody": every option needs a concrete value,
              // and null cannot be one.
              <Select<number>
                aria-label="Assigned agent"
                value={session.assignedToId ?? 0}
                disabled={assign.isPending}
                onChange={(next) => {
                  assign.mutate(next === 0 ? null : next);
                }}
                options={[
                  { value: 0, label: 'Unassigned' },
                  ...(agents.data?.items ?? [])
                    .filter((agent) => agent.status === 'active' && agent.userId !== null)
                    .map((agent) => ({
                      value: agent.userId as number,
                      label: agent.name || agent.email,
                    })),
                ]}
              />
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

function Row({ label, value }: { label: string; value: string | null }): React.JSX.Element | null {
  if (!value) return null;

  return (
    <div>
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="mt-0.5 break-words text-slate-700 dark:text-slate-200">{value}</dd>
    </div>
  );
}

export type { WireUserDto };
